import React, { useCallback, useRef } from 'react';
import { ImpactFeedbackStyle } from 'expo-haptics';
import {
  Pressable,
  type PressableStateCallbackType,
  type StyleProp,
  StyleSheet,
  type ViewStyle,
} from 'react-native';

import { theme } from '@garnish/constants';
import { useHovered } from '@garnish/core';
import { useUniqueNativeID } from '@garnish/core';
import { webOnlyStyles } from '@garnish/core';
import { BodyText } from '@garnish/core';
import { triggerHapticFeedback } from '@garnish/haptics';

import { type RadioSize, sizeStyles } from './RadioItem.config';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Renders a `Pressable` component with the `'radio'` role and multiple
 * customization options.
 */

export const RadioItem = <Value extends string>(props: RadioContainerItemProps<Value>) => {
  const {
    value,
    size = 'md',
    label,
    hint,
    isSelected,
    isDisabled,
    accessibilityLabel,
    'aria-label': ariaLabel,
    palette = 'kale',
    style,
    onPress,
  } = props;

  const pressableRef = useRef(null);
  const isHovered = useHovered(pressableRef);
  const labelNativeId = useUniqueNativeID();
  const hintNativeId = useUniqueNativeID();

  // ─── Helpers ─────────────────────────────────────────────────────────

  const handleOnPress = useCallback(() => {
    if (isSelected) return;

    onPress(value);
    void triggerHapticFeedback(ImpactFeedbackStyle.Light);
  }, [isSelected, onPress, value]);

  // ─── Styles ──────────────────────────────────────────────────────────

  const labelStyles = [
    styles.label,
    labelWebStyles,
    isSelected ? styles.labelSelected : undefined,
    isDisabled ? styles.labelDisabled : undefined,
  ];

  const pressableStyles = useCallback(
    (state: PressableStateCallbackType): StyleProp<ViewStyle> => {
      const { pressed } = state;

      return [
        styles.pressable,
        sizeStyles[size],
        pressableWebStyles,
        isSelected ? selectedPaletteStyles[palette] : undefined,
        isHovered && !isSelected ? styles.pressableHover : undefined,
        isDisabled ? styles.pressableDisabled : undefined,
        pressed ? styles.pressablePressed : undefined,
        style,
      ];
    },
    [size, isSelected, isHovered, isDisabled, palette, style],
  );

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Pressable
      ref={pressableRef}
      accessibilityRole="radio"
      accessibilityState={{
        selected: !isDisabled && isSelected,
        disabled: isDisabled,
      }}
      accessibilityLabel={accessibilityLabel}
      aria-label={ariaLabel}
      aria-selected={!isDisabled && isSelected}
      disabled={isDisabled}
      style={pressableStyles}
      onPress={handleOnPress}
    >
      <BodyText nativeID={labelNativeId} style={labelStyles} sizeMatch={['18']}>
        {label}
      </BodyText>

      {hint ? (
        <BodyText nativeID={hintNativeId} style={labelStyles} sizeMatch={['14']}>
          {hint}
        </BodyText>
      ) : null}
    </Pressable>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const selectedPaletteStyles: Record<RadioItemPalette, ViewStyle> = {
  kale: {
    backgroundColor: theme.colors.KALE,
  },
  spinach: {
    backgroundColor: theme.colors.SPINACH,
  },
};

const styles = StyleSheet.create({
  pressable: {
    alignItems: 'center',
    justifyContent: 'center',
    maxWidth: '100%',
    padding: theme.spacing['4'],
    borderRadius: theme.radius.large,
    backgroundColor: theme.colors.OPACITY.SPINACH.NEAR_LIGHTEST,
    minHeight: 72,
    userSelect: 'none',
  },
  pressableHover: {
    backgroundColor: theme.colors.OPACITY.KALE.LIGHTER,
  },
  pressablePressed: {
    opacity: 0.7,
  },
  pressableDisabled: {
    backgroundColor: theme.colors.OPACITY.DARK_KALE.ALMOST_TRANSPARENT,
  },

  label: {
    textAlign: 'center',
    color: theme.colors.TEXT_COLOR,
  },
  labelSelected: {
    color: theme.colors.CREAM,
  },
  labelDisabled: {
    color: theme.colors.OPACITY.DARK_KALE.DARK,
  },
});

const pressableWebStyles = webOnlyStyles({
  outlineColor: theme.colors.GRAY,
  outlineOffset: theme.spacing['1'],
  outlineWidth: 2,
  transition: theme.transitions.presets.colorsAndOpacity,
});
const labelWebStyles = webOnlyStyles({
  transition: theme.transitions.presets.colors,
});

type RadioContainerItemProps<Value extends string> = {
  label: string;
  hint?: string;
  accessibilityLabel?: string;
  'aria-label'?: string;
  value: Value;
  isSelected: boolean;
  isDisabled?: boolean;
  size?: RadioSize;
  palette?: RadioItemPalette;
  style?: StyleProp<ViewStyle>;
  onPress: (value: Value) => void;
};

type RadioItemPalette = 'kale' | 'spinach';
